import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { Branding } from '../_model/models';

@Injectable({ providedIn: 'root' })
export class BrandingService {
  private readonly baseUrl = `${environment.baseUrl}/branding`;

  /** Stream of the currently active branding. Header subscribes to this. */
  private activeBrandingSubject = new BehaviorSubject<Branding | null>(null);
  public activeBranding$ = this.activeBrandingSubject.asObservable();

  constructor(private http: HttpClient) {}

  // ── Active branding (header) ─────────────────────────────────────────────

  /** Fetch active branding from the API and push it into the stream. */
  refreshActiveBranding() {
    return this.http.get<any>(`${this.baseUrl}/active`).pipe(
      tap(function(res) {
        if (res && res.success) {
          this.activeBrandingSubject.next(res.data || null);
        }
      }.bind(this))
    );
  }

  /** Expose the current snapshot without subscribing. */
  get currentBranding(): Branding | null {
    return this.activeBrandingSubject.value;
  }

  /** Push a value directly (used after apply/delete). */
  setActiveBranding(branding: Branding | null): void {
    this.activeBrandingSubject.next(branding);
  }

  /** Clear on logout. */
  clearActiveBranding(): void {
    this.activeBrandingSubject.next(null);
  }

  // ── CRUD ─────────────────────────────────────────────────────────────────

  listBrandings() {
    return this.http.get<any>(`${this.baseUrl}`);
  }

  createBranding(payload: { name: string; isActive?: boolean }) {
    return this.http.post<any>(`${this.baseUrl}`, payload);
  }

  updateBranding(id: string, payload: { name: string }) {
    return this.http.put<any>(`${this.baseUrl}/${id}`, payload);
  }

  deleteBranding(id: string) {
    return this.http.delete<any>(`${this.baseUrl}/${id}`);
  }

  applyBranding(id: string) {
    return this.http.post<any>(`${this.baseUrl}/${id}/apply`, {}).pipe(
      tap(function(res) {
        if (res && res.success && res.data) {
          this.activeBrandingSubject.next(res.data);
        }
      }.bind(this))
    );
  }

  removeActiveBranding() {
    return this.http.post<any>(`${this.baseUrl}/remove-active`, {}).pipe(
      tap(function() {
        this.activeBrandingSubject.next(null);
      }.bind(this))
    );
  }

  uploadAsset(id: string, file: File, assetType: 'logo' | 'banner') {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('assetType', assetType);
    return this.http.post<any>(`${this.baseUrl}/${id}/asset`, fd);
  }
}
